import { useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';
import api from '@services/api';

import axios, { AxiosError } from 'axios';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import GetIpv4 from '@src/services/hooks/getIpV4/useGetIpv4';

import { getHomeRouteForLoggedInUser, isObjEmpty } from '@src/utility/Utils';
import { toastError } from '@src/utility/Toast';
import { handleLogin } from '@src/redux/actions/auth';
import { AbilityContext } from '@src/utility/context/Can';
import isFeatureEnabled from '@src/services/feature-control';
import isSigned from '@src/services/hooks/VerificationSigningTerms/useVerificationSigningTerms';
import { LoginFormData } from './LoginFormData';

type useLoginFormProps = {
  onChangeBlocking: (isBlocking: boolean) => void;
  setRefreshReCaptcha: React.Dispatch<React.SetStateAction<boolean>>;
  refreshReCaptcha: boolean;
};

export default function useLoginForm({
  onChangeBlocking,
  setRefreshReCaptcha,
  refreshReCaptcha,
}: useLoginFormProps) {
  const ability = useContext(AbilityContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .required('Email é obrigatório')
          .email('Por favor inclua um email válido'),
        password: yup.string().required('Por favor, insira sua senha'),
      }),
    []
  );

  const { register, handleSubmit, errors } = useForm<LoginFormData>({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (formValues: LoginFormData) => {
    if (isObjEmpty(errors)) {
      try {
        onChangeBlocking(true);
        let tokenRecaptcha = null;
        let ip_address = null;

        if (!executeRecaptcha) {
          console.log('Execute recaptcha not yet available');
        } else {
          tokenRecaptcha = await executeRecaptcha('login');
          ip_address = await GetIpv4();
        }

        const { data } = await api.post('login', {
          ...formValues,
          token_recaptcha: tokenRecaptcha,
          ip: ip_address,
        });
        api.defaults.headers.Authorization = `Bearer ${data.token}`;

        const { isTheSupport } = data;
        const signed = await isSigned(data.token);
        const user = {
          ...data.user,
          accessToken: data.token,
          refreshToken: null,
          requires2faAuth: data.requires_authentication,
          isTheSupport: data.isTheSupport,
          ismaster_accepted_platform_terms:
            data.ismaster_accepted_platform_terms,
        };

        onChangeBlocking(false);

        dispatch(handleLogin(user));
        ability.update(user.ability);

        const is2faEnabled = await isFeatureEnabled(
          'two_factor_authentication'
        );

        if (is2faEnabled && user.requires2faAuth) {
          const body = {
            email: user.email,
            limitResend: null,
          };

          await api.post('2fa/send-email', body);

          localStorage.setItem(`${user.iduser}_time_resend_code`, '120');

          history.push('/auth');
        } else if (
          user.ismaster === 0 &&
          !user.ismaster_accepted_platform_terms
        ) {
          history.push('/notice-of-terms');
        } else {
          history.push(getHomeRouteForLoggedInUser(user.role));
        }
        // }
      } catch (error) {
        setRefreshReCaptcha(!refreshReCaptcha);
        onChangeBlocking(false);

        let message = null;
        const err = error as Error | AxiosError;

        if (axios.isAxiosError(err)) {
          message =
            err.response?.data.message || 'Algo deu errado ao efetuar o login';
        } else {
          message = 'Server error';
        }

        toastError({ title: 'Atenção', description: message });
      }
    }
  };

  return {
    register,
    errors,
    onSubmit: handleSubmit(onSubmit),
  };
}
